/**
 * ResultsTabStrip — URL-driven tab navigation for the Engagement Results page.
 *
 * State lives in the route (`/engagements/:id/results/<tab-key>`) so the active
 * tab survives refresh, deep-linking, and browser-back. We deliberately do NOT
 * use the Radix `<Tabs>` primitive — NavLink already provides `aria-current`
 * for the matched child route, and routing must stay the source of truth.
 *
 * Visual: a segmented "pill" bar (shadcn/OriginUI aesthetic). Text-only tabs on
 * an inset muted track; the active tab is a raised pill (background + layered
 * shadow) with a smooth hover transition on inactive tabs. All styling uses
 * existing Tailwind theme tokens — no hardcoded colors, no new dependencies.
 */

import { NavLink } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { cn } from "@/lib/utils";

interface ResultsTabStripProps {
  engagementId: string;
}

const TABS = [
  { key: "balance-sheet", labelKey: "engagement.results.tabs.balanceSheet" },
  { key: "movement", labelKey: "engagement.results.tabs.movement" },
  { key: "pl-impact", labelKey: "engagement.results.tabs.plImpact" },
  {
    key: "journal-entries",
    labelKey: "engagement.results.tabs.journalEntries",
  },
  { key: "pajes", labelKey: "engagement.results.tabs.pajes" },
  { key: "contracts", labelKey: "engagement.results.tabs.contracts" },
] as const;

export function ResultsTabStrip({ engagementId }: ResultsTabStripProps) {
  const { t } = useTranslation();
  return (
    <div className="overflow-x-auto">
      <div
        data-testid="results-tab-strip"
        className={cn(
          "inline-flex w-max items-center gap-1 whitespace-nowrap",
          "rounded-lg border border-border/60 bg-muted p-1",
          "shadow-[inset_0_1px_2px_rgba(0,0,0,0.16)]",
        )}
      >
        {TABS.map((tab) => (
          <NavLink
            key={tab.key}
            to={`/engagements/${engagementId}/results/${tab.key}`}
            end
            data-testid={`results-tab-${tab.key}`}
            className={({ isActive }) =>
              cn(
                "rounded-md px-3.5 py-2 text-sm font-medium",
                "outline-offset-2 transition-all duration-150",
                "focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring/70",
                isActive
                  ? "bg-background text-foreground shadow-[0_1px_2px_rgba(0,0,0,0.22),0_3px_10px_rgba(0,0,0,0.14)]"
                  : "text-muted-foreground hover:bg-background/50 hover:text-foreground",
              )
            }
          >
            {t(tab.labelKey)}
          </NavLink>
        ))}
      </div>
    </div>
  );
}
